import React from 'react';
import './ShopCarousel.css'; // Utilise le CSS du carrousel qui contient aussi les styles de carte

// --- Copie/Adaptation des fonctions de ShopModal.js pour le statut ---
const daysOfWeek = [
  { key: 'sunday', label: 'Dimanche' }, { key: 'monday', label: 'Lundi' }, 
  { key: 'tuesday', label: 'Mardi' }, { key: 'wednesday', label: 'Mercredi' }, 
  { key: 'thursday', label: 'Jeudi' }, { key: 'friday', label: 'Vendredi' }, 
  { key: 'saturday', label: 'Samedi' }
];

// Fonction simplifiée pour vérifier si dans une plage horaire
const checkTimeInRange = (now, hours) => {
  if (!hours) return false;
  const currentHour = now.getHours();
  const currentMinutes = now.getMinutes();
  const currentTimeInMinutes = currentHour * 60 + currentMinutes;

  const checkPeriod = (period) => {
    if (!period?.open || !period?.close) return false;
    const [openHour, openMinutes] = period.open.split(':').map(Number);
    const [closeHour, closeMinutes] = period.close.split(':').map(Number);
    const openTime = openHour * 60 + openMinutes;
    // Gérer le cas où la fermeture est après minuit (ex: 23:00 - 01:00)
    let closeTime = closeHour * 60 + closeMinutes;
    if (closeTime < openTime) { // Fermeture le lendemain
        // Si l'heure actuelle est après l'ouverture OU avant la fermeture (du lendemain)
        return currentTimeInMinutes >= openTime || currentTimeInMinutes <= closeTime;
    } else { // Fermeture le même jour
        return currentTimeInMinutes >= openTime && currentTimeInMinutes <= closeTime;
    }
  };

  return checkPeriod(hours.morning) || checkPeriod(hours.afternoon);
};


// Fonction principale pour déterminer si ouvert
const isShopOpenNow = (shop) => {
  const { hours, holidays, specialHours } = shop;
  if (!hours) return { isOpen: false, label: 'Horaires indisponibles' }; // Cas où il n'y a pas d'horaires

  const now = new Date();
  const today = now.toISOString().split('T')[0]; // Format YYYY-MM-DD

  if (holidays && holidays[today]) {
    return { isOpen: false, label: 'Fermé (Férié)' };
  }

  if (specialHours && specialHours[today]) {
    const specialToday = specialHours[today];
    const isOpen = checkTimeInRange(now, specialToday);
    return { isOpen, label: isOpen ? 'Ouvert (Horaires Spéciaux)' : 'Fermé (Horaires Spéciaux)' };
  }

  const currentDayKey = daysOfWeek[now.getDay()].key;
  const todayHours = hours[currentDayKey];
  const isOpen = checkTimeInRange(now, todayHours);

  return { isOpen, label: isOpen ? 'Ouvert' : 'Fermé' };
};
// --- Fin des fonctions de statut ---

// --- Nouvelle fonction pour formater la distance ---
const formatDistance = (distanceInKm) => {
  if (distanceInKm == null || !isFinite(distanceInKm)) {
    return null; // Ne rien afficher si la distance n'est pas valide
  }
  if (distanceInKm < 1) {
    // Afficher en mètres si moins de 1 km
    return `${Math.round(distanceInKm * 1000)} m`;
  } else {
    // Afficher en km avec une décimale sinon
    return `${distanceInKm.toFixed(1)} km`;
  }
};

// Icône Navigation/GPS SVG simple
const GpsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
    <path fillRule="evenodd" d="M9.293 2.293a1 1 0 011.414 0l7 7a1 1 0 01-1.414 1.414L10 4.414l-6.293 6.293a1 1 0 01-1.414-1.414l7-7z" clipRule="evenodd" />
    <path fillRule="evenodd" d="M9.293 7.293a1 1 0 011.414 0l7 7a1 1 0 01-1.414 1.414L10 9.414l-6.293 6.293a1 1 0 01-1.414-1.414l7-7z" clipRule="evenodd" />
  </svg>
);

const ShopCard = ({ shop, isActive, onClick, onInfoClick }) => {
  
  if (!shop) return null;

  const shopTitle = shop?.titre || '';
  const displayName = shopTitle.length > 30 ? shopTitle.substring(0, 27) + '...' : shopTitle;
  const address = shop?.address || '';
  const status = isShopOpenNow(shop);
  const formattedDistance = formatDistance(shop?.distance); // Appel de la fonction de formatage

  const getDirectionsUrl = () => {
    if (!shop?.address && !shop?.titre) return '#';
    const query = encodeURIComponent(`${shop.titre || ''}, ${shop.address || ''}`.trim());
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  };

  return (
    <div 
      className={`shop-card ${isActive ? 'active' : ''}`} 
      onClick={() => onClick(shop)}
      role="button" tabIndex={0} onKeyPress={(e) => e.key === 'Enter' && onClick(shop)} 
    >

        {/* Titre directement enfant */}
        <h4 className="shop-card-title">{displayName || 'Nom indisponible'}</h4>

        {/* Adresse directement enfant */}
        {address && address.trim() !== '' && <p className="shop-card-city">{address}</p>}

        {/* Affichage conditionnel de la distance formatée */}
        {formattedDistance && (
          <p className="shop-card-distance">📍 {formattedDistance}</p>
        )}

        {/* Footer directement enfant, contient statut et bouton */}
        <div className="shop-card-footer">
          {/* Statut avec bouton info */} 
          <div className="shop-status-container">
            <span className={`shop-status ${status.isOpen ? 'open' : 'closed'}`}>
              {status.label}
            </span>
            <button 
              className="shop-status-info-btn"
              onClick={(e) => {
                e.stopPropagation();
                onInfoClick && onInfoClick(shop);
              }}
              aria-label="Voir les horaires"
            >
              🕐 Horaires
            </button>
          </div>

          {/* Bouton GPS */} 
          {(address && address.trim() !== '') && ( 
            <a 
              href={getDirectionsUrl()}
              target="_blank" 
              rel="noopener noreferrer" 
              className="shop-card-button gps"
              onClick={(e) => e.stopPropagation()} 
              aria-label={`Itinéraire vers ${displayName}`}
            >
              <GpsIcon />
              Y aller
            </a>
          )}
        </div> 
    </div>
  );
};

export default ShopCard; 